import React from 'react';
import { matchPath } from 'react-router';

import {
  OperationsClosureSkeleton,
  PlanSectionSkeleton,
  Shimmer,
  TargetsConfigSkeleton,
} from './phConfigSkeletons';
import SeasonCardSkeleton from './phSeasonCardSkeleton';

/**
 * Full-screen skeletons for every Production Head route. Each one reproduces the outer chrome of
 * the real page (wrapper background, padding, cards, headings) so the layout does not jump when
 * the real screen replaces it. Heights come from the real classes: text-xs 16px, text-sm 20px,
 * text-base 24px, text-lg/xl 28px, .bodySm 22.4px, .bodyXs 16.8px, .bodyMd 24px,
 * CustomButton h-11 (lg:h-12). Text/button *widths* are approximations — they depend on
 * translated strings — and nothing here has been measured in a browser.
 */

/** A single text line: a box of the real line height with a slightly shorter bar inside it. */
const Line = ({
  height,
  className,
  barClassName = 'h-3',
}: {
  /** Tailwind height class for the real line box, e.g. `h-5`. */
  height: string;
  /** Width / spacing classes for the bar. */
  className: string;
  barClassName?: string;
}) => (
  <div className={`flex items-center ${height}`}>
    <Shimmer className={`${barClassName} ${className}`} />
  </div>
);

// ───────────────────────────── /production-season ─────────────────────────────

export const SeasonsListScreenSkeleton = () => (
  <div
    className="relative flex min-h-[calc(100vh-70px)] flex-col bg-[#f3f4f4] px-3 py-6 md:px-5 md:py-10"
    aria-hidden
  >
    {/* Status tabs — same wrapper classes as the real tab strip */}
    <div className="-ml-[12px] -mr-[12px] -mt-[24px] mb-2.5 flex items-center justify-between overflow-x-auto bg-[#fff] px-3 py-3 pb-0 shadow-[inset_20px_0_20px_-20px_rgba(0,0,0,0.1),inset_-20px_0_20px_-20px_rgba(0,0,0,0.1)] md:-ml-[20px] md:-mr-[20px] md:-mt-[40px] md:px-5 max-xl:-ml-[10px] max-xl:-mr-[10px]">
      <div className="scrollbar-hide flex flex-1 space-x-2 overflow-x-auto border-b border-neutral-200 pr-2 sm:space-x-4 sm:pr-4">
        {['w-20', 'w-14', 'w-14'].map((width, idx) => (
          // px-2 py-2 text-xs (16px) → 32px tall; sm:px-4 sm:text-sm (20px) → 36px tall
          <div
            key={idx}
            className="flex h-8 shrink-0 items-center px-2 sm:h-9 sm:px-4"
          >
            <Shimmer className={`h-3 ${width}`} />
          </div>
        ))}
      </div>
    </div>
    <div className="flex-1">
      <SeasonCardSkeleton count={3} />
    </div>
  </div>
);

// ───────────────────────────── /season-settings ─────────────────────────────

type SettingsTab =
  | 'season-plan'
  | 'operational-plan'
  | 'harvest-recovery-plan'
  | 'targets'
  | 'operations-closure';

const SETTINGS_TAB_WIDTHS = ['w-24', 'w-36', 'w-28', 'w-14', 'w-32'];

export const SeasonSettingsScreenSkeleton = ({ tab = 'season-plan' }: { tab?: SettingsTab }) => {
  let content: React.ReactNode;
  if (tab === 'targets') content = <TargetsConfigSkeleton />;
  else if (tab === 'operations-closure') content = <OperationsClosureSkeleton />;
  else content = <PlanSectionSkeleton count={1} />;

  return (
    <div
      className="relative flex min-h-screen flex-col bg-gray-100 px-3 py-3 md:px-5 md:py-5"
      aria-hidden
    >
      {/* Season selection card: bodySm label (22.4px) + h-11 / lg:h-12 dropdown, max-w-md */}
      <div className="mb-4 rounded-lg bg-white p-4 shadow-sm md:mb-6 md:p-6">
        <div className="w-full max-w-md">
          <div className="mb-1 flex h-[22.4px] items-center">
            <Shimmer className="h-3 w-16" />
          </div>
          <Shimmer className="h-11 w-full rounded-lg lg:h-12" />
        </div>
      </div>

      <div>
        {/* Tab bar: tabs are px-1 py-4 text-base (24px) → 56px tall; refresh is h-8 w-8 */}
        <div className="ph-configbar w-full rounded-lg border-b border-gray-200 bg-white shadow-sm">
          <div className="flex items-stretch">
            <div className="scrollbar-hide relative flex flex-1 gap-8 overflow-x-auto px-4 md:px-6">
              {SETTINGS_TAB_WIDTHS.map((width, idx) => (
                <div
                  key={idx}
                  className="flex shrink-0 items-center px-1 py-4"
                >
                  <Shimmer className={`h-4 ${width}`} />
                </div>
              ))}
            </div>
            <div className="flex shrink-0 items-center px-3">
              <Shimmer className="h-8 w-8 rounded-full" />
            </div>
          </div>
        </div>

        <div>
          <div className="ph-config-content mb-6 rounded-lg">{content}</div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────── /season-settings/:seasonVersionId (Season Config files) ───────────────────

const FileIconSkeleton = () => <Shimmer className="h-10 w-10 shrink-0 rounded-lg" />;

export const SeasonVersionFilesScreenSkeleton = ({ sections = 8 }: { sections?: number }) => (
  <div
    className="min-h-screen bg-[#f3f4f4] px-3 py-3 sm:px-4 sm:py-4 md:px-5 md:py-5"
    aria-hidden
  >
    <div className="space-y-4 rounded-lg border bg-white p-3 sm:p-4">
      {Array.from({ length: sections }).map((_, idx) => (
        <div
          key={idx}
          className="rounded-lg bg-white p-2"
        >
          {/* h2 text-lg (28px) mb-1 */}
          <div className="mb-1 flex h-7 items-center">
            <Shimmer className="h-4 w-40" />
          </div>
          <div className="space-y-3">
            <div className="flex flex-col gap-3 rounded-lg border border-gray-200 p-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-3">
                <FileIconSkeleton />
                <div className="min-w-0">
                  {/* h3 text-sm (20px) + p text-xs (16px) */}
                  <Line
                    height="h-5"
                    className="w-48"
                  />
                  <Line
                    height="h-4"
                    className="w-56"
                    barClassName="h-2.5"
                  />
                </div>
              </div>
              {/* Download: px-3 py-2 text-sm → 20 + 16 + 2 (border) = 38px */}
              <Shimmer className="h-[38px] w-28 shrink-0 self-start rounded-lg sm:self-auto" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ───────────── /season-settings/operational/:id and /season-settings/harvest/:id ─────────────

export const PlanVersionFilesScreenSkeleton = ({
  sections = 1,
  itemsPerSection = 5,
}: {
  sections?: number;
  itemsPerSection?: number;
}) => (
  <div
    className="min-h-screen bg-[#f3f4f4] px-5 py-5"
    aria-hidden
  >
    <div className="space-y-4 rounded-lg border bg-white p-4">
      {Array.from({ length: sections }).map((_, sectionIdx) => (
        <div
          key={sectionIdx}
          className="rounded-lg bg-white p-2"
        >
          {/* h2 text-lg (28px) mb-3 */}
          <div className="mb-3 flex h-7 items-center">
            <Shimmer className="h-4 w-52" />
          </div>
          <div className="space-y-3">
            {Array.from({ length: itemsPerSection }).map((__, itemIdx) => (
              <div
                key={itemIdx}
                className="flex items-center justify-between rounded-lg border border-gray-200 p-3"
              >
                <div className="flex items-center space-x-3">
                  <FileIconSkeleton />
                  <div>
                    <Line
                      height="h-5"
                      className="w-56"
                    />
                    <Line
                      height="h-4"
                      className="w-16"
                      barClassName="h-2.5"
                    />
                  </div>
                </div>
                {/* Download: px-4 py-2 text-sm → 38px */}
                <Shimmer className="h-[38px] w-28 shrink-0 rounded-lg" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ───────────────────────────── /season-details ─────────────────────────────

const SeasonHeaderSkeleton = () => (
  <div className="mx-3 rounded-lg bg-white p-4 sm:mx-4 sm:p-5 md:mx-6 md:p-6">
    {/* Title + start/end date chips (text-lg/xl = 28px; bodyXs chips ≈ 23px) */}
    <div className="mb-4 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
      <div className="flex h-7 items-center sm:mr-4">
        <Shimmer className="h-5 w-36" />
      </div>
      <div className="flex flex-wrap gap-2 sm:gap-3">
        <Shimmer className="h-[23px] w-44 rounded-lg" />
        <Shimmer className="h-[23px] w-44 rounded-lg" />
      </div>
    </div>

    {/* Crop · Goal (yield) · Target (area) — .bodyMd rows are 24px */}
    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
      {['w-24', 'w-64', 'w-64'].map((width, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && (
            <span
              aria-hidden
              className="mx-1 my-auto hidden h-6 w-px bg-gray-300 sm:block"
            />
          )}
          <Line
            height="h-6"
            className={width}
            barClassName="h-4"
          />
        </React.Fragment>
      ))}
    </div>

    {/* Season label · Agronomist */}
    <div className="pt-2">
      <div className="mt-3 flex flex-wrap items-center gap-3 sm:gap-4">
        <Line
          height="h-6"
          className="w-64"
          barClassName="h-4"
        />
        <span
          aria-hidden
          className="mx-1 my-auto hidden h-6 w-px bg-gray-300 sm:block"
        />
        <Line
          height="h-6"
          className="w-56"
          barClassName="h-4"
        />
      </div>
    </div>
  </div>
);

const ProductionCycleCardSkeleton = () => (
  <div className="overflow-hidden rounded-lg border border-neutralLight">
    {/* px-2.5 py-3.5 header: region name (.bodyMdSemiBold 24px) + status badge + chevron */}
    <div className="flex items-center justify-between bg-[linear-gradient(270deg,_#FFFFFF_-1.49%,_#EEF8EC_100%)] px-2.5 py-3.5">
      <div className="flex items-center gap-2">
        <Line
          height="h-6"
          className="w-24"
          barClassName="h-4"
        />
        <Shimmer className="h-[22px] w-20 rounded" />
      </div>
      <Shimmer className="h-5 w-5" />
    </div>
    {/* p-2.5 > p-4 sm:p-6 md:p-10: .h4 number (32px × 1.3) + mb-1 + .bodyXs caption (16.8px) */}
    <div className="p-2.5">
      <div className="flex flex-col items-center p-4 sm:p-6 md:p-10">
        <div className="mb-1 flex h-[41.6px] items-center justify-center">
          <Shimmer className="h-7 w-16" />
        </div>
        <Line
          height="h-[16.8px]"
          className="w-28"
          barClassName="h-2.5"
        />
      </div>
    </div>
  </div>
);

export const SeasonDetailScreenSkeleton = ({ cards = 4 }: { cards?: number }) => (
  <div
    className="min-h-full bg-gray-100 py-3 sm:py-4 md:py-5"
    aria-hidden
  >
    <SeasonHeaderSkeleton />

    {/* ProductionCycles card */}
    <div className="fontColor mx-3 my-3 rounded-lg bg-white p-4 shadow-sm sm:mx-4 sm:my-4 sm:p-5 md:mx-7 md:my-5 md:p-6">
      <div className="mb-4 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:mb-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between md:mb-8">
        <div className="flex h-7 items-center">
          <Shimmer className="h-5 w-20" />
        </div>
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 md:gap-8">
          <Line
            height="h-6"
            className="w-56"
            barClassName="h-4"
          />
          <Line
            height="h-6"
            className="w-40"
            barClassName="h-4"
          />
          {/* Publish: h-10 w-28 sm:h-12 sm:w-32 */}
          <Shimmer className="h-10 w-28 rounded-lg sm:h-12 sm:w-32" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
        {Array.from({ length: cards }).map((_, idx) => (
          <ProductionCycleCardSkeleton key={idx} />
        ))}
      </div>
    </div>
  </div>
);

// ───────────────────────────── /distribute-targets ─────────────────────────────

const TABLE_CELL = 'px-3 py-2 sm:px-4 md:px-6 md:py-3';
const TABLE_HEAD_CELL = 'px-3 py-3 sm:px-4 md:px-6 md:py-4';
const SHADED = 'bg-[#d9d9d933]';

export const DistributeTargetsScreenSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div
    className="min-h-full bg-gray-100 py-3 sm:py-4 md:py-5"
    aria-hidden
  >
    <div className="mx-2 my-2 sm:mx-3 sm:my-3 md:mx-4 md:my-4">
      <div className="w-full rounded-lg bg-white p-3 sm:p-4 md:p-6">
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[700px] border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                {/* .bodySm header cells (22.4px); target yield/area add a 20px unit line */}
                {['w-16', 'w-24', 'w-24'].map((width, idx) => (
                  <th
                    key={idx}
                    className={`${TABLE_HEAD_CELL} text-left`}
                  >
                    <Line
                      height="h-[22.4px]"
                      className={width}
                    />
                  </th>
                ))}
                {[0, 1].map((idx) => (
                  <th
                    key={idx}
                    className={`${TABLE_HEAD_CELL} ${SHADED}`}
                  >
                    <div className="flex flex-col items-center">
                      <Line
                        height="h-[22.4px]"
                        className="w-24"
                      />
                      <Line
                        height="h-5"
                        className="w-12"
                      />
                    </div>
                  </th>
                ))}
                <th className={TABLE_HEAD_CELL}>
                  <div className="flex justify-center">
                    <Line
                      height="h-[22.4px]"
                      className="w-16"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }).map((_, rowIdx) => (
                <tr
                  key={rowIdx}
                  className="border-b border-gray-200 last:border-b-0"
                >
                  <td className={TABLE_CELL}>
                    <Line
                      height="h-[16.8px]"
                      className="w-24"
                    />
                  </td>
                  {/* "See more" CustomButton !h-8 px-3 */}
                  <td className={TABLE_CELL}>
                    <Shimmer className="h-8 w-20 rounded-lg" />
                  </td>
                  <td className={TABLE_CELL}>
                    <Shimmer className="h-8 w-20 rounded-lg" />
                  </td>
                  <td className={`${TABLE_CELL} ${SHADED}`}>
                    <div className="flex justify-center">
                      <Line
                        height="h-[16.8px]"
                        className="w-8"
                      />
                    </div>
                  </td>
                  <td className={`${TABLE_CELL} ${SHADED}`}>
                    <div className="flex justify-center">
                      <Line
                        height="h-[16.8px]"
                        className="w-8"
                      />
                    </div>
                  </td>
                  {/* Live button h-8 */}
                  <td className={TABLE_CELL}>
                    <div className="flex items-center justify-center gap-2">
                      <Shimmer className="h-8 w-20 rounded-lg" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals row */}
        <div className="mr-0 mt-4 flex justify-end sm:mr-[5%] md:mr-[15%]">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 md:gap-8">
            <Line
              height="h-[16.8px]"
              className="w-10"
            />
            <Line
              height="h-[22.4px]"
              className="w-32"
            />
            <Line
              height="h-[22.4px]"
              className="w-32"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ───────────── /season-settings/:seasonId/:planId/:versionId/:revisionId ─────────────

export const RevisionDetailScreenSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <div
    className="min-h-screen bg-gray-50 p-6"
    aria-hidden
  >
    {/* "Version: … · Revision: …" line (text-sm, mb-4) */}
    <div className="mb-4 flex h-5 items-center">
      <Shimmer className="h-3.5 w-72" />
    </div>
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="items-center justify-between border-b border-gray-200 p-6">
        <div className="flex h-7 items-center">
          <Shimmer className="h-5 w-48" />
        </div>
      </div>
      <div className="space-y-2 p-6">
        {Array.from({ length: rows }).map((_, idx) => (
          // DefaultSeasonBar card: title (.bodyMdSemiBold 24px) + caption (.bodyXs 16.8px).
          // Its exact outer padding is not mirrored here, so the row height is approximate.
          <div
            key={idx}
            className="flex items-center justify-between rounded-lg border border-neutralLight p-4"
          >
            <div className="flex items-center gap-3">
              <Shimmer className="h-10 w-10 shrink-0 rounded-lg" />
              <div>
                <Line
                  height="h-6"
                  className="w-56"
                  barClassName="h-4"
                />
                <Line
                  height="h-[16.8px]"
                  className="w-32"
                  barClassName="h-2.5"
                />
              </div>
            </div>
            <Shimmer className="h-11 w-28 shrink-0 rounded-lg lg:h-12" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ───────────────────── /production-season/create and /edit/:seasonId ─────────────────────

const FormFieldSkeleton = ({
  labelWidth,
  className = 'max-w-[496px]',
}: {
  labelWidth: string;
  className?: string;
}) => (
  <div className={`w-full ${className}`}>
    <div className="mb-1 flex h-[22.4px] items-center">
      <Shimmer className={`h-3 ${labelWidth}`} />
    </div>
    <Shimmer className="h-12 w-full rounded-xl" />
  </div>
);

export const SeasonFormScreenSkeleton = () => (
  <div
    className="min-h-screen bg-gray-50"
    aria-hidden
  >
    <div className="mx-auto px-2 py-3">
      <div className="space-y-6 rounded-xl bg-white p-4 shadow-md sm:p-6 md:p-8">
        {/* "Fields marked * are mandatory" note (text-sm, 20px) */}
        <Line
          height="h-5"
          className="w-64"
        />
        <FormFieldSkeleton labelWidth="w-28" />
        <FormFieldSkeleton labelWidth="w-16" />
        <div className="flex flex-wrap gap-4 pb-8">
          <FormFieldSkeleton
            labelWidth="w-20"
            className="max-w-[240px]"
          />
          <FormFieldSkeleton
            labelWidth="w-20"
            className="max-w-[240px]"
          />
        </div>
        <FormFieldSkeleton labelWidth="w-36" />
        {/* Season goals: label, description, yield row, "or" divider, area row */}
        <div>
          <div className="mb-1 flex h-[22.4px] items-center">
            <Shimmer className="h-3 w-24" />
          </div>
          <div className="mb-3 flex h-[16.8px] items-center">
            <Shimmer className="h-2.5 w-80 max-w-full" />
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <Shimmer className="h-12 w-72 rounded-xl" />
            <Shimmer className="h-5 w-16" />
            <Shimmer className="h-12 w-72 rounded-xl" />
          </div>
        </div>
        <div className="flex justify-end gap-4 pt-6">
          <Shimmer className="h-12 w-[120px] rounded-lg" />
        </div>
      </div>
    </div>
  </div>
);

// ───────────────────────────── route → skeleton registry ─────────────────────────────

const isSettingsTab = (value: string | null): value is SettingsTab =>
  value === 'season-plan' ||
  value === 'operational-plan' ||
  value === 'harvest-recovery-plan' ||
  value === 'targets' ||
  value === 'operations-closure';

/**
 * The skeleton for a Production Head route, or `null` for any other path (the caller then keeps
 * its generic loader). Matched most-specific first because `/season-settings/:seasonVersionId`
 * would otherwise swallow the `operational` / `harvest` sub-routes.
 */
export const getProductionHeadRouteSkeleton = (
  pathname: string,
  search: string = ''
): React.ReactNode | null => {
  const exact = (path: string) => matchPath({ path, end: true }, pathname);

  if (exact('/production-season')) return <SeasonsListScreenSkeleton />;
  if (exact('/production-season/create') || exact('/production-season/edit/:seasonId')) {
    return <SeasonFormScreenSkeleton />;
  }
  if (exact('/season-settings')) {
    const tab = new URLSearchParams(search).get('tab');
    return <SeasonSettingsScreenSkeleton tab={isSettingsTab(tab) ? tab : 'season-plan'} />;
  }
  if (exact('/season-settings/operational/:id') || exact('/season-settings/harvest/:id')) {
    return <PlanVersionFilesScreenSkeleton />;
  }
  if (exact('/season-settings/:seasonId/:planId/:versionId/:revisionId')) {
    return <RevisionDetailScreenSkeleton />;
  }
  if (exact('/season-settings/:seasonVersionId')) return <SeasonVersionFilesScreenSkeleton />;
  if (exact('/season-details')) return <SeasonDetailScreenSkeleton />;
  if (exact('/distribute-targets')) return <DistributeTargetsScreenSkeleton />;

  return null;
};
